import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { toast } from 'react-hot-toast';
import { File, Paperclip, Search, Send, Trash2 } from 'lucide-react';
import {
  Avatar,
  Banner,
  Button,
  Field,
  Input,
  SegmentedControl,
  SlideOver,
  SlideOverFooter,
  Textarea,
} from '@/components/ui';
import { ApiCallError } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { usePatient } from '@/hooks/usePatients';
import { usePatientPicker, type PatientPickerItem } from '@/hooks/usePatientPicker';
import { useSendPatientMessage } from '@/hooks/usePatientMessages';
import { useAttachMessageDocument, validateMessageAttachmentFile } from '@/hooks/useMessageAttachments';
import { useUploadDocument } from '@/hooks/useDocuments';
import {
  useGrantPatientPortalAccess,
  usePatientPortalInvitation,
} from '@/hooks/usePatientPortal';
import { useSendSms, useSmsClinic } from '@/hooks/useSms';
import {
  patientMessageComposeNotice,
  type PatientMessageComposeNotice,
} from '@/lib/patientMessageComposeNotice';
import { sendPatientMessageCompose } from '@/lib/sendPatientMessageCompose';
import { patientSmsSendBlockReason } from '@/lib/smsCompose';
import { formatPatientTelephoneNumbers, primaryTelephoneNumber } from '@/lib/patientStamkortFields';
import { personDisplayName } from '@/utils/messageSender';
import { cn } from '@/utils/cn';
import { TYPE } from '@/lib/typography';

export type NewMessageChannel = 'messages' | 'sms';

/** Shared Ny besked slide-over. `patientId` + `patientName` + `clinicId` + `onClose` stay compatible with `MessageModalProps`. */
export interface NewMessageSlideOverProps {
  open: boolean;
  onClose: () => void;
  clinicId: string;
  patientId?: string | null;
  patientName?: string;
  patientEmail?: string | null;
  allowChangePatient?: boolean;
  onSent?: () => void;
}

type ComposePatient = {
  id: string;
  name: string;
  email: string | null;
  phone: string | null;
};

function composeNoticeText(
  notice: PatientMessageComposeNotice,
  t: TFunction,
): { description: string; warning: string | null; offerInvite: boolean } {
  switch (notice.kind) {
    case 'sms':
      return {
        description: t(
          'messages.compose.description_sms',
          'SMS sendes til patientens telefonnummer. Der sendes ikke en e-mail.',
        ),
        warning: null,
        offerInvite: false,
      };
    case 'pick_patient':
      return {
        description: t(
          'messages.compose.description',
          'Vælg en patient. Beskeden vises i patientportalen, når patienten har adgang. En kort e-mail sendes, hvis patienten har en e-mailadresse.',
        ),
        warning: null,
        offerInvite: false,
      };
    case 'loading':
      return {
        description: t(
          'messages.compose.description_loading',
          'Henter patientens e-mail og portaladgang…',
        ),
        warning: null,
        offerInvite: false,
      };
    case 'unconfirmed':
      return {
        description: t(
          'messages.compose.description_unconfirmed',
          'Kunne ikke bekræfte e-mail og portaladgang. Beskeden gemmes, og en e-mail sendes kun, hvis patienten har en e-mailadresse.',
        ),
        warning: null,
        offerInvite: false,
      };
    case 'portal_and_email':
      return {
        description: t(
          'messages.compose.description_portal_and_email',
          'Beskeden vises i patientens patientportal, og patienten får en e-mail om, at der er en ny besked.',
        ),
        warning: null,
        offerInvite: false,
      };
    case 'portal_no_email':
      return {
        description: t(
          'messages.compose.description_portal_no_email',
          'Beskeden vises i patientportalen. Der sendes ingen e-mail, fordi patienten ikke har en e-mailadresse.',
        ),
        warning: t(
          'messages.compose.warning_no_email',
          'Patienten har ingen e-mail, så der sendes ingen e-mailnotifikation.',
        ),
        offerInvite: false,
      };
    case 'email_no_portal':
      return {
        description: t(
          'messages.compose.description_email_no_portal',
          'Patienten får en e-mail om, at der er en ny besked, med link til patientportalen. Beskeden kan først læses, når patienten har portaladgang.',
        ),
        warning: t(
          'messages.compose.warning_no_portal',
          'Patienten har ikke portaladgang og kan ikke læse beskeden endnu.',
        ),
        offerInvite: notice.offerInvite,
      };
    case 'email_no_portal_pending':
      return {
        description: t(
          'messages.compose.description_email_no_portal_pending',
          'Patienten får en e-mail om, at der er en ny besked, med link til patientportalen. En portalinvitation er allerede sendt.',
        ),
        warning: t(
          'messages.compose.warning_no_portal_pending',
          'Patienten har ikke portaladgang endnu. En invitation er allerede sendt.',
        ),
        offerInvite: false,
      };
    case 'neither':
      return {
        description: t(
          'messages.compose.description_neither',
          'Beskeden gemmes, men patienten har ingen e-mail og ingen portaladgang. Patienten får ikke besked og kan ikke læse beskeden.',
        ),
        warning: t(
          'messages.compose.warning_neither',
          'Patienten har ingen e-mail og ingen portaladgang.',
        ),
        offerInvite: false,
      };
    default: {
      const _exhaustive: never = notice;
      return _exhaustive;
    }
  }
}

function pickerToCompose(patient: PatientPickerItem): ComposePatient {
  return {
    id: patient.id,
    name: personDisplayName(patient.first_name, patient.last_name),
    email: patient.email,
    phone: primaryTelephoneNumber(patient.telephone_subscribers),
  };
}

export function NewMessageSlideOver({
  open,
  onClose,
  clinicId,
  patientId = null,
  patientName = '',
  patientEmail = null,
  allowChangePatient = true,
  onSent,
}: NewMessageSlideOverProps) {
  const { t } = useTranslation();
  const { hasPermission } = useAuth();
  const sendMessage = useSendPatientMessage();
  const sendSms = useSendSms();
  const uploadDocument = useUploadDocument();
  const attachDocument = useAttachMessageDocument(undefined);
  const smsClinic = useSmsClinic(clinicId);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [patient, setPatient] = useState<ComposePatient | null>(() =>
    patientId ? { id: patientId, name: patientName, email: patientEmail, phone: null } : null,
  );
  const [picking, setPicking] = useState(!patientId);
  const [search, setSearch] = useState('');
  const [channel, setChannel] = useState<NewMessageChannel>('messages');
  const [message, setMessage] = useState('');
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const detail = usePatient(patient?.id);
  const portalInvite = usePatientPortalInvitation(patient?.id);
  const grantPortal = useGrantPatientPortalAccess(patient?.id ?? '');
  const picker = usePatientPicker(clinicId, search);
  const canSms = hasPermission(clinicId, 'sms', 'create');
  const smsEnabled = smsClinic.data?.settings.enabled ?? false;
  const consentAccepted =
    ((detail.data?.sms_consent ?? {}) as { status?: string }).status === 'accepted';
  const resolvedPhone =
    primaryTelephoneNumber(detail.data?.telephone_subscribers) ?? '';
  const resolvedEmail = detail.data?.email ?? patient?.email ?? null;
  const resolvedName = personDisplayName(detail.data?.first_name, detail.data?.last_name) || patient?.name || '';
  const notice = patientMessageComposeNotice({
    channel,
    patientSelected: Boolean(patient),
    detailsReady: Boolean(detail.data),
    detailsError: detail.isError,
    hasEmail: Boolean(detail.data?.email?.trim()),
    hasPortalAccess: Boolean(detail.data?.portal_user_id),
    portalInvitePending: Boolean(portalInvite.data),
    inviteResolved: !patient || portalInvite.isSuccess || portalInvite.isError,
    patientActive: detail.data?.status === 'active',
  });
  const noticeText = composeNoticeText(notice, t);

  const grantPortalAccess = async () => {
    if (!patient) return;
    try {
      const result = await grantPortal.mutateAsync();
      if (result.status === 'linked') {
        toast.success(t('patients.portal.toast_linked', { email: result.email }));
      } else {
        toast.success(t('patients.portal.toast_invited', { email: result.email }));
      }
    } catch (err) {
      if (err instanceof ApiCallError) {
        if (err.code === 'conflict') {
          toast.error(t('patients.portal.error_already_has_access'));
        } else if (err.code === 'invalid-input') {
          toast.error(err.message);
        } else {
          toast.error(t('patients.portal.error_grant_failed'));
        }
      } else {
        toast.error(t('patients.portal.error_grant_failed'));
      }
    }
  };

  useEffect(() => {
    if (!open) return;
    if (patientId) {
      setPatient({ id: patientId, name: patientName, email: patientEmail, phone: null });
      setPicking(false);
    } else {
      setPatient(null);
      setPicking(true);
    }
    setSearch('');
    setChannel('messages');
    setMessage('');
    setSelectedFiles([]);
    setSending(false);
    setError(null);
  }, [open, patientId, patientName, patientEmail]);

  const dirty = Boolean(message.trim() || selectedFiles.length > 0 || (patient && patient.id !== patientId));
  const canSmsChannel = canSms;
  const channelOptions = useMemo(() => {
    const options: { value: NewMessageChannel; label: string }[] = [
      { value: 'messages', label: t('messages.compose.channel_messages', 'Beskeder') },
    ];
    if (canSmsChannel) {
      options.push({ value: 'sms', label: t('messages.compose.channel_sms', 'SMS') });
    }
    return options;
  }, [canSmsChannel, t]);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    const valid: File[] = [];
    for (const file of files) {
      const validationError = validateMessageAttachmentFile(file);
      if (validationError === 'size_limit_exceeded') {
        toast.error(t('documents.errors.size_limit', `Filen ${file.name} er for stor (max 50 MB)`));
      } else if (validationError === 'invalid_type') {
        toast.error(t('documents.errors.invalid_type', `Filtypen for ${file.name} er ikke tilladt`));
      } else {
        valid.push(file);
      }
    }
    setSelectedFiles((prev) => [...prev, ...valid]);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const sendDisabled =
    sending ||
    !patient ||
    (channel === 'messages' && !message.trim() && selectedFiles.length === 0) ||
    (channel === 'sms' && !message.trim());

  const handleSubmit = async () => {
    if (!patient) {
      setError(t('messages.compose.patient_required', 'Vælg en patient.'));
      return;
    }
    const trimmed = message.trim();
    setError(null);
    if (channel === 'sms') {
      const block = patientSmsSendBlockReason({
        body: trimmed,
        phone: resolvedPhone,
        consentAccepted,
        smsEnabled,
        hasCredit: (smsClinic.data?.credits.balance ?? 0) > 0,
      });
      switch (block) {
        case null:
          break;
        case 'empty':
          setError(t('sms.body_required', 'SMS-tekst er påkrævet.'));
          return;
        case 'no_phone':
          setError(t('sms.no_phone', 'Patienten har intet telefonnummer.'));
          return;
        case 'disabled':
          setError(t('sms.not_enabled_title', 'SMS er ikke aktiveret for denne klinik'));
          return;
        case 'no_consent':
          setError(t('patients.communication.sms_needs_consent', 'SMS kræver at patienten er tilmeldt.'));
          return;
        case 'no_credit':
          setError(t('sms.no_credit', 'Klinikken har ingen SMS-kredit. Køb en pakke under Indstillinger → SMS.'));
          return;
        default: {
          const _exhaustive: never = block;
          return _exhaustive;
        }
      }
    } else if (!trimmed && selectedFiles.length === 0) {
      return;
    }

    setSending(true);
    try {
      if (channel === 'sms') {
        await sendSms.mutateAsync({
          clinic_id: clinicId,
          patient_id: patient.id,
          subscriber_identifier: resolvedPhone,
          body: trimmed,
          trigger: 'manual',
          patient_name: resolvedName || undefined,
        });
        toast.success(t('sms.send_success', 'SMS sendt.'));
      } else {
        await sendPatientMessageCompose({
          patientId: patient.id,
          content: trimmed,
          files: selectedFiles,
          sendMessage: (params) => sendMessage.mutateAsync(params),
          uploadDocument: (params) => uploadDocument.mutateAsync(params),
          attachDocument: (params) => attachDocument.mutateAsync(params),
        });
        toast.success(t('messages.toasts.sent', 'Besked sendt'));
      }
      onSent?.();
      onClose();
    } catch (err) {
      console.error('Error sending message:', err);
      setError(
        channel === 'sms'
          ? t('sms.send_failed', 'SMS-afsendelse fejlede.')
          : t('messages.errors.send_failed', 'Kunne ikke sende besked'),
      );
    } finally {
      setSending(false);
    }
  };

  return (
    <SlideOver
      open={open}
      title={t('messages.compose.title', 'Ny besked')}
      description={noticeText.description}
      onClose={onClose}
      dirty={dirty}
      footer={
        <SlideOverFooter
          onCancel={onClose}
          primary={
            <Button
              type="button"
              onClick={() => void handleSubmit()}
              disabled={sendDisabled}
              loading={sending}
              loadingText={t('messages.compose.sending', 'Sender…')}
              aria-describedby={!patient ? 'new-message-to' : undefined}
            >
              <Send />
              {t('messages.compose.send', 'Send besked')}
            </Button>
          }
        />
      }
    >
      <div className="flex flex-col gap-s4">
        {error ? (
          <p className={cn(TYPE.small, 'font-medium text-ds-red-ink')} role="alert">
            {error}
          </p>
        ) : null}

        {noticeText.warning ? (
          <Banner
            tone="warning"
            action={
              noticeText.offerInvite ? (
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  onClick={() => void grantPortalAccess()}
                  disabled={sending || grantPortal.isPending}
                  loading={grantPortal.isPending}
                  loadingText={t('patients.portal.granting', 'Sender invitation…')}
                >
                  {t('patients.portal.grant', 'Giv adgang til patientportal')}
                </Button>
              ) : undefined
            }
          >
            {noticeText.warning}
          </Banner>
        ) : null}

        <Field label={t('messages.compose.to', 'Til')} htmlFor="new-message-to">
          {patient && !picking ? (
            <div
              id="new-message-to"
              className="flex items-center gap-s3 rounded-ctl bg-ds-sunk px-s3 py-s2"
            >
              <Avatar name={resolvedName} size={36} />
              <div className="min-w-0 flex-1">
                <p className={cn(TYPE.control, 'font-semibold text-ds-text')}>{resolvedName}</p>
                {resolvedEmail ? (
                  <p className={cn(TYPE.small, 'truncate text-ds-muted')}>{resolvedEmail}</p>
                ) : null}
              </div>
              {allowChangePatient ? (
                <Button type="button" variant="tertiary" size="sm" onClick={() => setPicking(true)}>
                  {t('messages.compose.change_patient', 'Skift patient')}
                </Button>
              ) : null}
            </div>
          ) : (
            <div className="space-y-s2">
              <Input
                id="new-message-to"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={t('messages.inbox.select_patient_placeholder', 'Søg efter en patient...')}
                leadingIcon={<Search className="h-4 w-4" />}
                autoComplete="off"
              />
              <ul className="max-h-56 divide-y divide-ds-line overflow-y-auto rounded-ctl bg-ds-sunk">
                {(picker.data ?? []).length === 0 ? (
                  <li className={cn(TYPE.small, 'px-s3 py-s4 text-center text-ds-muted')}>
                    {t('messages.inbox.no_patient_matches', 'Ingen matchende patienter')}
                  </li>
                ) : (
                  (picker.data ?? []).map((item) => (
                    <li key={item.id}>
                      <button
                        type="button"
                        onClick={() => {
                          setPatient(pickerToCompose(item));
                          setPicking(false);
                          setSearch('');
                          setError(null);
                        }}
                        className="flex w-full flex-col px-s3 py-s2 text-left hover:bg-ds-neutral-tint"
                      >
                        <span className={cn(TYPE.control, 'font-semibold text-ds-text')}>
                          {personDisplayName(item.first_name, item.last_name)}
                        </span>
                        {(item.email ||
                          formatPatientTelephoneNumbers(item.telephone_subscribers, item.phone)) && (
                          <span className={cn(TYPE.small, 'text-ds-muted')}>
                            {[
                              item.email,
                              formatPatientTelephoneNumbers(item.telephone_subscribers, item.phone),
                            ]
                              .filter(Boolean)
                              .join(' · ')}
                          </span>
                        )}
                      </button>
                    </li>
                  ))
                )}
              </ul>
            </div>
          )}
        </Field>

        <Field label={t('messages.compose.channel', 'Kanal')}>
          <SegmentedControl
            ariaLabel={t('messages.compose.channel', 'Kanal')}
            options={channelOptions}
            value={channel}
            onChange={setChannel}
          />
        </Field>

        <Textarea
          id="new-message-body"
          label={t('messages.compose.message', 'Besked')}
          required
          rows={6}
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          disabled={sending}
          className="min-h-24"
        />

        {channel === 'messages' ? (
          <div>
            <p className={cn(TYPE.label, 'text-ds-text')}>
              {t('messages.compose.attachments', 'Vedhæftede filer')}
            </p>
            <p className={cn(TYPE.small, 'mt-s1 text-ds-muted')}>
              {t(
                'messages.compose.attachments_hint',
                'PDF eller billeder, maks. 50 MB pr. fil.',
              )}
            </p>
            <input
              ref={fileInputRef}
              type="file"
              onChange={handleFileSelect}
              multiple
              accept=".pdf,.jpg,.jpeg,.png,.gif,.webp,.doc,.docx,.xls,.xlsx,.txt"
              className="hidden"
            />
            <Button
              type="button"
              variant="secondary"
              size="sm"
              className="mt-s2"
              onClick={() => fileInputRef.current?.click()}
              disabled={sending}
            >
              <Paperclip />
              {t('messages.compose.attach_file', 'Vedhæft fil')}
            </Button>
            {selectedFiles.length > 0 ? (
              <ul className="mt-s3 space-y-s2">
                {selectedFiles.map((file, index) => (
                  <li
                    key={`${file.name}-${index}`}
                    className="flex items-center justify-between gap-s2 rounded-ctl bg-ds-sunk px-s3 py-s2"
                  >
                    <span className="flex min-w-0 items-center gap-s2">
                      <File className="h-4 w-4 shrink-0 text-ds-muted" aria-hidden />
                      <span className={cn(TYPE.small, 'truncate text-ds-text')}>{file.name}</span>
                    </span>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => setSelectedFiles((prev) => prev.filter((_, i) => i !== index))}
                      disabled={sending}
                      aria-label={t('messages.compose.remove_file', 'Fjern fil')}
                    >
                      <Trash2 />
                    </Button>
                  </li>
                ))}
              </ul>
            ) : null}
          </div>
        ) : null}
      </div>
    </SlideOver>
  );
}
